'use client'

import { useMemo } from 'react'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { LayersIcon, ListFilterIcon } from 'lucide-react'

import { directionForChange, EMPTY_VALUE } from '~/app/accounting/display-utils'
import { buildTokenFilterKey } from '~/app/accounting/filters/helpers'
import {
  buildTokenNetChange,
  TokenNetChange,
} from '~/app/accounting/netChangeBreakdown'
import { Period } from '~/app/accounting/period-utils'
import {
  FlatToken,
  useAccountingTokenPositions,
} from '~/app/accounting/useAccountingTokenPositions'
import { Table } from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
  TableActionTrigger,
} from '~/components/TableActions'
import {
  CurrencyAmount,
  TokenBalance,
  TokenBalanceContainer,
  tokenBalanceFormatters,
  type TokenBalanceProps,
  TokenDisplay,
} from '~/components/component-library'
import { Dropdown } from '~/components/component-library/Dropdown'
import { buildTokenInfoDropdownItems } from '~/components/smart-accounts/tokenDropdownItems'
import { useAuth } from '~/context/AuthProvider'
import { getNumberFromBigNumber } from '~/utils/numbers'
import { getVaultProviderName, isLegacyVault } from '~/utils/vault'

type TokenRow = {
  key: string
  address: FlatToken['address']
  chainId: FlatToken['chainId']
  symbol: string
  startUsd: number
  endUsd: number
} & TokenNetChange

// One row per token across the union of start and end holdings.
export const buildTokenRows = (
  startTokens: FlatToken[],
  endTokens: FlatToken[],
): TokenRow[] => {
  const startByKey = new Map(
    startTokens.map((t) => [`${t.chainId}:${t.address}`, t]),
  )
  const endByKey = new Map(
    endTokens.map((t) => [`${t.chainId}:${t.address}`, t]),
  )

  // Union of start + end token keys (end first = current holdings).
  const keys = new Set<string>([...endByKey.keys(), ...startByKey.keys()])

  const rows = Array.from(keys).map((key): TokenRow => {
    // Earn rows are measured in $1 underlying units (yield reads as flow);
    // row identity stays the held vault token.
    const netChange = buildTokenNetChange({
      start: startByKey.get(key),
      end: endByKey.get(key),
    })
    const token = (netChange.end ?? netChange.start) as FlatToken
    return {
      key,
      address: token.address,
      chainId: token.chainId,
      symbol: token.symbol,
      startUsd: netChange.start?.usdValue ?? 0,
      endUsd: netChange.end?.usdValue ?? 0,
      ...netChange,
    }
  })

  // Largest current holdings first.
  return rows.sort((a, b) => b.endUsd - a.endUsd)
}

const columnHelper = createColumnHelper<TokenRow>()

// Earn cells can't use TokenBalance: it re-converts position amounts at the
// current vault rate, but ours are boundary-correct underlying units. Compose
// the same two lines; TokenDisplay resolves the vault address to its position
// identity (USDC ⚡).
const AmountCell = ({
  row,
  tokenAmount,
  usdValue,
  direction,
}: {
  row: TokenRow
  tokenAmount: bigint
  usdValue: number | undefined
  direction: TokenBalanceProps['direction']
}) => {
  const absAmount = tokenAmount < 0n ? -tokenAmount : tokenAmount
  if (row.underlying)
    return (
      <TokenBalanceContainer>
        {/* Earn rows only convert when every side is priced, so usdValue is set. */}
        <CurrencyAmount
          layout="standard"
          size="small"
          value={usdValue!}
          direction={direction}
        />
        <span className="flex h-3 items-center text-xs leading-4 text-secondary">
          {tokenBalanceFormatters.formatAbbreviatedTokenAmount(
            getNumberFromBigNumber(absAmount, row.underlying.decimals),
            { address: row.underlying.asset, chainId: row.chainId },
          )}
          <span className="ml-1">
            <TokenDisplay
              address={row.address}
              chainId={row.chainId}
              layout="inline-text"
            />
          </span>
        </span>
      </TokenBalanceContainer>
    )
  return (
    <TokenBalance
      address={row.address}
      chainId={row.chainId}
      tokenAmount={absAmount}
      layout="standard"
      size="small"
      direction={direction}
      usdValue={usdValue}
    />
  )
}

const BalanceCell = ({
  row,
  token,
}: {
  row: TokenRow
  token: FlatToken | undefined
}) =>
  token ? (
    <AmountCell
      row={row}
      tokenAmount={BigInt(token.rawBalance)}
      usdValue={token.usdValue}
      direction="neutral"
    />
  ) : (
    EMPTY_VALUE
  )

const GainLossValue = ({ value }: { value: number | undefined }) =>
  value != null ? (
    <CurrencyAmount
      layout="standard"
      size="small"
      value={value}
      direction={directionForChange(value)}
    />
  ) : (
    EMPTY_VALUE
  )

// Unpriced rows render "-" rather than falling through to TokenBalance's
// live-price fallback, which would contradict the "-" gain/loss beside it
// and the period-end-price tooltip.
const NetFlowCell = ({ row }: { row: TokenRow }) =>
  row.flowUsd != null ? (
    <AmountCell
      row={row}
      tokenAmount={row.tokenChange}
      usdValue={row.flowUsd}
      direction={directionForChange(row.flowUsd)}
    />
  ) : (
    EMPTY_VALUE
  )

// Info-only actions — rows aggregate accounts, so there's no single account
// for Send/Buy/Sell.
const ActionsCell = ({
  row,
  onFilterTransactionsByToken,
  onViewLots,
}: {
  row: TokenRow
  onFilterTransactionsByToken: (tokenKey: string) => void
  onViewLots?: (tokenKey: string) => void
}) => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const tokenKey = buildTokenFilterKey({
    chainId: row.chainId,
    address: row.address,
  })
  return (
    <div className="flex justify-end">
      <Dropdown
        align="end"
        side="bottom"
        items={[
          ...(onViewLots
            ? [
                {
                  content: 'View lots',
                  icon: <LayersIcon />,
                  onClick: () => onViewLots(tokenKey),
                },
              ]
            : []),
          {
            content: 'Filter transactions by token',
            icon: <ListFilterIcon />,
            onClick: () => onFilterTransactionsByToken(tokenKey),
          },
          ...buildTokenInfoDropdownItems({
            token: row.address,
            chainId: row.chainId,
            setGlobalModalState,
            closeGlobalModal,
          }),
        ]}
      >
        <TableActionTrigger aria-label="Token actions" />
      </Dropdown>
    </div>
  )
}

const NET_FLOW_TOOLTIP = (
  <>
    Value of tokens moved in and out
    {/* block — the tooltip body isn't flex, so force the break */}
    <span className="block text-secondary">Valued at the period-end price</span>
  </>
)
const GAIN_LOSS_TOOLTIP = 'Value change from price movement'

// Accessor (not display) columns so every column sorts. A factory (not a
// constant) so the cells can close over the filter callback and search query.
const buildTokenColumns = ({
  onFilterTransactionsByToken,
  onViewLots,
  highlightText,
}: {
  onFilterTransactionsByToken: (tokenKey: string) => void
  onViewLots?: (tokenKey: string) => void
  highlightText?: string
}) =>
  [
    columnHelper.accessor((row) => row.symbol, {
      id: 'token',
      header: 'Token',
      size: 300,
      minSize: 240,
      maxSize: 480,
      cell: ({ row }) => (
        <TokenDisplay
          address={row.original.address}
          chainId={row.original.chainId}
          layout="two-line"
          size="small"
          protocol={
            isLegacyVault(row.original.address)
              ? getVaultProviderName(row.original.address)
              : undefined
          }
          highlightText={highlightText}
        />
      ),
      // Own stacking context so TokenDisplay's network badge can't paint over
      // the sticky header. On the <td> so the column's width isn't disturbed.
      meta: { cellClassName: 'isolate' },
    }),
    columnHelper.accessor((row) => row.startUsd, {
      id: 'startBalance',
      header: 'Starting balance',
      size: 200,
      minSize: 180,
      maxSize: 360,
      cell: ({ row }) => (
        <BalanceCell row={row.original} token={row.original.start} />
      ),
      meta: { align: 'right' },
    }),
    columnHelper.accessor((row) => row.endUsd, {
      id: 'endBalance',
      header: 'Ending balance',
      size: 200,
      minSize: 180,
      maxSize: 360,
      cell: ({ row }) => (
        <BalanceCell row={row.original} token={row.original.end} />
      ),
      meta: { align: 'right' },
    }),
    columnHelper.accessor((row) => row.flowUsd ?? 0, {
      id: 'netFlow',
      header: 'Change from flow',
      size: 180,
      minSize: 160,
      maxSize: 260,
      cell: ({ row }) => <NetFlowCell row={row.original} />,
      meta: { align: 'right', headerTooltip: NET_FLOW_TOOLTIP },
    }),
    columnHelper.accessor((row) => row.gainLossUsd ?? 0, {
      id: 'gainLoss',
      header: 'Change from price',
      size: 170,
      minSize: 150,
      maxSize: 260,
      cell: ({ row }) => <GainLossValue value={row.original.gainLossUsd} />,
      meta: { align: 'right', headerTooltip: GAIN_LOSS_TOOLTIP },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      ...TABLE_ACTION_COLUMN_WIDTH,
      cell: ({ row }) => (
        <ActionsCell
          row={row.original}
          onFilterTransactionsByToken={onFilterTransactionsByToken}
          onViewLots={onViewLots}
        />
      ),
      meta: TABLE_ACTION_COLUMN_META,
    }),
  ] as ColumnDef<TokenRow>[]

export const TokensTab = ({
  period,
  accountIds,
  chainIds,
  search,
  onFilterTransactionsByToken,
  onViewLots,
}: {
  period: Period | null
  accountIds?: string[]
  chainIds?: number[]
  search?: string
  onFilterTransactionsByToken: (tokenKey: string) => void
  onViewLots?: (tokenKey: string) => void
}) => {
  const { startTokens, endTokens, startLoading, endLoading, hasError } =
    useAccountingTokenPositions({ period, accountIds, chainIds })

  const columns = useMemo(
    () =>
      buildTokenColumns({
        onFilterTransactionsByToken,
        onViewLots,
        highlightText: search,
      }),
    [onFilterTransactionsByToken, onViewLots, search],
  )

  const allRows = useMemo(
    () => buildTokenRows(startTokens, endTokens),
    [startTokens, endTokens],
  )

  // Positions are already client-side; filter by symbol/address locally.
  const rows = useMemo(() => {
    const query = search?.trim().toLowerCase()
    if (!query) return allRows
    return allRows.filter(
      (row) =>
        row.symbol.toLowerCase().includes(query) ||
        row.address.toLowerCase().includes(query),
    )
  }, [allRows, search])

  const isLoading = startLoading || endLoading

  // The balance summary above owns the single error surface for this tab (it
  // shares the same query), so render nothing here on error rather than a
  // duplicate, full-bleed banner.
  if (hasError) return null

  return (
    <Table
      size="small"
      columns={columns}
      data={rows}
      emptyMessage={
        search
          ? `No tokens match "${search}"`
          : `No token balances found for this period`
      }
      onRowClick={
        onViewLots
          ? (row) =>
              onViewLots(
                buildTokenFilterKey({
                  chainId: row.original.chainId,
                  address: row.original.address,
                }),
              )
          : undefined
      }
      pagination={{ enabled: false }}
      showColumnFilters={false}
      showSorting
      isLoading={isLoading}
      skeletonCellClassName="h-[34px]"
    />
  )
}
